import { getSiteTitlePrefix } from "@/utils/appEnvironment";

export type SiteIdentityEnv = Pick<ImportMetaEnv, "VITE_SITE_TITLE_PREFIX" | "VITE_FAVICON_LABEL">;

const FAVICON_MAX_CHARS = 4;
/** Font size in the 64-unit favicon, indexed by label length, so up to 4 characters fit. */
const FAVICON_FONT_SIZES = [0, 44, 36, 26, 20];
/** Relative luminance above which black text out-contrasts white (WCAG). */
const DARK_TEXT_LUMINANCE = 0.179;

const XML_ENTITIES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&apos;",
};

/** VITE_FAVICON_LABEL without control characters (invalid in SVG), trimmed and cut to 4 characters; "" when blank. */
export function getFaviconLabel({
  VITE_FAVICON_LABEL,
}: Pick<ImportMetaEnv, "VITE_FAVICON_LABEL">): string {
  const text = (VITE_FAVICON_LABEL ?? "").replace(/\p{Cc}/gu, "").trim();
  return Array.from(text).slice(0, FAVICON_MAX_CHARS).join("").trim();
}

/** FNV-1a hash, finished with murmur3's fmix32 so labels differing in one character spread across hues. */
export function labelHue(label: string): number {
  let hash = 0x811c9dc5;
  for (const char of label) hash = Math.imul(hash ^ char.codePointAt(0)!, 0x01000193) >>> 0;
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x85ebca6b) >>> 0;
  hash ^= hash >>> 13;
  hash = Math.imul(hash, 0xc2b2ae35) >>> 0;
  hash ^= hash >>> 16;
  return (hash >>> 0) % 360;
}

function relativeLuminance(hue: number, saturation: number, lightness: number): number {
  const a = saturation * Math.min(lightness, 1 - lightness);
  const channel = (n: number) => {
    const k = (n + hue / 30) % 12;
    const c = lightness - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(0) + 0.7152 * channel(8) + 0.0722 * channel(4);
}

/** A rounded square with the label in bold, as an SVG data URL. */
export function faviconDataUrl(label: string): string {
  const hue = labelHue(label);
  const textColor = relativeLuminance(hue, 0.7, 0.45) > DARK_TEXT_LUMINANCE ? "#000" : "#fff";
  const fontSize = FAVICON_FONT_SIZES[Math.min(Array.from(label).length, FAVICON_MAX_CHARS)];
  const text = label.replace(/[&<>"']/g, (char) => XML_ENTITIES[char]);
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">` +
    `<rect width="64" height="64" rx="12" fill="hsl(${hue},70%,45%)"/>` +
    `<text x="32" y="32" dy=".35em" text-anchor="middle" font-family="system-ui,sans-serif" ` +
    `font-weight="800" font-size="${fontSize}" fill="${textColor}">${text}</text></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/** Prefixes the page title and swaps in a labelled favicon; with neither env var set it touches nothing. */
export function applySiteIdentity(env: SiteIdentityEnv): void {
  const prefix = getSiteTitlePrefix(env);
  if (prefix) document.title = `${prefix} | ${document.title}`;

  const label = getFaviconLabel(env);
  if (!label) return;
  const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (icon) icon.href = faviconDataUrl(label);
}
